/*
 * Project: bellcloud
 * File: apps/frontend/src/api/dashboard/useDashboard.ts
 * Description: Dashboard API Hooks — static summary + alarm history
 */

import { useQuery } from '@tanstack/react-query';
import { apiHelpers, extractApiData } from '@/api/core';
import {
  POLL_DASHBOARD_ALARM_HISTORY_MS,
  POLL_DASHBOARD_LIVE_MS,
  POLL_DASHBOARD_STATIC_MS,
  STALE_DASHBOARD_ALARM_HISTORY_MS,
  STALE_DASHBOARD_STATIC_MS,
} from '@/api/query/polling';
import type {
  DashboardStaticData,
  DashboardAlarmHistoryData,
  DashboardQueryOptions,
  DashboardReportData,
  LiveAggregationResult,
} from '@packages/types/dashboard';

// ─── Query Keys ───────────────────────────────────────────────────────────────

const dashboardKeys = {
  all: ['dashboard'] as const,
  static: (companyId?: number, siteId?: number) =>
    [...dashboardKeys.all, 'static', companyId, siteId] as const,
  alarmHistory: (companyId?: number, siteId?: number) =>
    [...dashboardKeys.all, 'alarm-history', companyId, siteId] as const,
  liveAggregation: (companyId?: number, siteId?: number) =>
    [...dashboardKeys.all, 'live-aggregation', companyId, siteId] as const,
  report: (companyId?: number, siteId?: number) =>
    [...dashboardKeys.all, 'report', companyId, siteId] as const,
};

// ─── Hooks ────────────────────────────────────────────────────────────────────

/**
 * 정적 집계 (deviceStatus.total + bumpCal)
 * - 5분마다 폴링
 * - 백엔드에서 Redis 5분 캐시
 */
export const useDashboardStatic = (options: DashboardQueryOptions = {}) => {
  const { companyId, siteId, enabled = true } = options;
  
  return useQuery<DashboardStaticData>({
    queryKey: dashboardKeys.static(companyId, siteId),
    queryFn: async () => {
      const queryParams: Record<string, string | number> = {};
      if (companyId) queryParams.company_id = companyId;
      if (siteId) queryParams.site_id = siteId;

      const response = await apiHelpers.get<DashboardStaticData>(
        'dashboard.static', undefined, queryParams
      );
      return extractApiData(response);
    },
    enabled,
    refetchInterval: POLL_DASHBOARD_STATIC_MS,
    staleTime: STALE_DASHBOARD_STATIC_MS,
  });
};

/**
 * 최근 10분 알람 이력 (liveAlarms.in10Min)
 * - 60초마다 폴링
 * - 백엔드에서 Redis 1분 캐시
 */
export const useDashboardAlarmHistory = (options: DashboardQueryOptions = {}) => {
  const { companyId, siteId, enabled = true } = options;
  
  return useQuery<DashboardAlarmHistoryData>({
    queryKey: dashboardKeys.alarmHistory(companyId, siteId),
    queryFn: async () => {
      const queryParams: Record<string, string | number> = {};
      if (companyId) queryParams.company_id = companyId;
      if (siteId) queryParams.site_id = siteId;

      const response = await apiHelpers.get<DashboardAlarmHistoryData>(
        'dashboard.alarmHistory', undefined, queryParams
      );
      return extractApiData(response);
    },
    enabled,
    refetchInterval: POLL_DASHBOARD_ALARM_HISTORY_MS,
    staleTime: STALE_DASHBOARD_ALARM_HISTORY_MS,
  });
};

/**
 * 실시간 집계 (monitoring/latest 기반)
 * - 5초마다 폴링
 * - 백엔드에서 deviceStatus, liveAlarms.now, liveEvents를 사이트별로 계산
 */
export const useDashboardLiveAggregation = (options: DashboardQueryOptions = {}) => {
  const { companyId, siteId, enabled = true } = options;

  return useQuery<LiveAggregationResult>({
    queryKey: dashboardKeys.liveAggregation(companyId, siteId),
    queryFn: async () => {
      const queryParams: Record<string, string | number> = {};
      if (companyId) queryParams.company_id = companyId;
      if (siteId) queryParams.site_id = siteId;

      const response = await apiHelpers.get<LiveAggregationResult>(
        'dashboard.liveAggregation', undefined, queryParams
      );
      return extractApiData(response);
    },
    enabled,
    refetchInterval: POLL_DASHBOARD_LIVE_MS,
  });
};

/**
 * 리포트용 기기 상세 목록 (on-demand, 폴링 없음)
 * Excel/PDF 다운로드 시 호출
 */
export const fetchDashboardReport = async (
  companyId?: number,
  siteId?: number,
): Promise<DashboardReportData> => {
  const queryParams: Record<string, string | number> = {};
  if (companyId) queryParams.company_id = companyId;
  if (siteId) queryParams.site_id = siteId;

  const response = await apiHelpers.get<DashboardReportData>(
    'dashboard.report',
    undefined,
    queryParams,
  );
  return extractApiData(response);
};
